import { motion } from "framer-motion";
import { BarChart3, Eye, Users, TrendingUp, MousePointerClick, Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const projections = [
  { icon: Eye, label: "Vues mensuelles estimées", value: 1200, suffix: "+", description: "Visiteurs uniques sur votre portfolio" },
  { icon: Users, label: "Prospects qualifiés", value: 45, suffix: "/mois", description: "Contacts professionnels intéressés" },
  { icon: MousePointerClick, label: "Taux d'engagement", value: 8.5, suffix: "%", description: "vs 2% moyenne du marché", decimals: 1 },
  { icon: Globe, label: "Pays atteints", value: 12, suffix: "+", description: "Visibilité internationale" },
];

const weeklyData = [
  { day: "Lun", views: 45 },
  { day: "Mar", views: 62 },
  { day: "Mer", views: 78 },
  { day: "Jeu", views: 55 },
  { day: "Ven", views: 90 },
  { day: "Sam", views: 42 },
  { day: "Dim", views: 35 },
];

function AnimatedNumber({ value, suffix, decimals = 0 }: { value: number; suffix: string; decimals?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const done = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !done.current) {
        done.current = true;
        const t0 = Date.now();
        const run = () => {
          const p = Math.min((Date.now() - t0) / 1500, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setCount(Number((eased * value).toFixed(decimals)));
          if (p < 1) requestAnimationFrame(run);
        };
        run();
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, decimals]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const maxViews = Math.max(...weeklyData.map((d) => d.views));

const SellAnalyticsPreview = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <BarChart3 className="w-4 h-4" />
            Projections analytics
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Ce que votre portfolio peut{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              vous apporter
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Projections basées sur les données réelles de nos clients sur 6 mois
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4">
            {projections.map((p, i) => (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-5 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 hover:border-primary/30 transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                  <p.icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-2xl md:text-3xl font-extrabold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  <AnimatedNumber value={p.value} suffix={p.suffix} decimals={p.decimals} />
                </p>
                <p className="text-sm font-semibold mt-1">{p.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Mini chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-bold text-lg">Trafic hebdomadaire</h3>
                <p className="text-xs text-muted-foreground">Projection type — premier mois</p>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                <TrendingUp className="w-3.5 h-3.5" />
                +127%
              </div>
            </div>

            <div className="flex items-end gap-3 h-48 mb-4">
              {weeklyData.map((d, i) => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                  <motion.div
                    className="w-full rounded-t-lg bg-gradient-to-t from-primary to-accent/70"
                    initial={{ height: 0 }}
                    whileInView={{ height: `${(d.views / maxViews) * 100}%` }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.08, duration: 0.6 }}
                  />
                  <span className="text-xs text-muted-foreground">{d.day}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border/30">
              <div>
                <p className="text-xs text-muted-foreground">Total semaine</p>
                <p className="text-xl font-bold">407 <span className="text-sm text-muted-foreground font-normal">vues</span></p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Meilleur jour</p>
                <p className="text-xl font-bold">Ven <span className="text-sm text-primary font-normal">90 vues</span></p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.p
          className="text-center text-xs text-muted-foreground mt-8 max-w-lg mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          * Projections basées sur les données moyennes de nos 50+ clients actifs. Les résultats varient selon le secteur, la localisation et l'activité de partage.
        </motion.p>
      </div>
    </section>
  );
};

export default SellAnalyticsPreview;
